import React from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import {
    createRoutesFromChildren,
    matchRoutes,
    useLocation,
    useNavigationType,
} from 'react-router-dom'
import App from './App.tsx'
import './index.css'
// Import debug utility in production for testing
import './utils/sentryDebug'
import { addNetworkMonitoring } from './utils/networkRetry'

// PII fields to sanitize from error reports
const PII_FIELDS = [
    'firstName', 'lastName', 'email', 'phone', 'dateOfBirth',
    'birthCity', 'currentCity', 'birthCountry', 'currentCountry',
    'idDocumentFile', 'receiptFile', 'certificateFile',
    'idDocumentUrl', 'paymentReceiptUrl', 'certificateUrl'
];

function sanitizeData(data: any): any {
    if (!data || typeof data !== 'object') return data;

    const sanitized = { ...data };
    PII_FIELDS.forEach(field => {
        if (sanitized[field]) {
            sanitized[field] = '[REDACTED]';
        }
    });

    // Recursively sanitize nested objects
    Object.keys(sanitized).forEach(key => {
        if (typeof sanitized[key] === 'object' && sanitized[key] !== null) {
            sanitized[key] = sanitizeData(sanitized[key]);
        }
    });

    return sanitized;
}

function sanitizeEvent(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
    // Filter out Facebook in-app browser WebView errors
    // These are third-party errors from Facebook's JavaScript SDK that we cannot control
    const errorMessage = event.message || event.exception?.values?.[0]?.value || '';
    
    // Check for Facebook WebView "Java object is gone" errors
    if (errorMessage.includes('Java object is gone') || 
        errorMessage.includes('enableButtonsClickedMetaDataLogging')) {
        return null; // Don't send this error to Sentry
    }
    
    // Sanitize extra data
    if (event.extra) {
        event.extra = sanitizeData(event.extra);
    }

    // Sanitize breadcrumbs
    if (event.breadcrumbs) {
        event.breadcrumbs.forEach(breadcrumb => {
            if (breadcrumb.data) {
                breadcrumb.data = sanitizeData(breadcrumb.data);
            }
        });
    }

    // Sanitize contexts
    if (event.contexts) {
        event.contexts = sanitizeData(event.contexts);
    }

    return event;
}

// Initialize Sentry only in production
if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
    Sentry.init({
        dsn: import.meta.env.VITE_SENTRY_DSN,
        environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'production',
        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration({
                maskAllText: false,
                blockAllMedia: false,
            }),
            Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
            Sentry.reactRouterV6BrowserTracingIntegration({
                useEffect: React.useEffect,
                useLocation,
                useNavigationType,
                createRoutesFromChildren,
                matchRoutes,
            }),
        ],
        enableLogs: true,
        tracesSampleRate: 1.0, // 100% performance monitoring
        replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
        replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.

        beforeSend: sanitizeEvent,
        // Release tracking
        release: import.meta.env.VITE_SENTRY_RELEASE || 'unknown',
        // Additional options for mobile optimization
        maxBreadcrumbs: 50,
        attachStacktrace: true,
        // Filter out third-party errors from Facebook's in-app browser and other WebView bridges
        ignoreErrors: [
            // Facebook in-app browser WebView errors
            /Java object is gone/i,
            /enableButtonsClickedMetaDataLogging/i,
            // Other common Facebook WebView bridge errors
            /FBNativeJSBridge/i,
            /fb_channel/i,
            /webkit\.messageHandlers/i,
        ],
        // Enhanced mobile error context
        beforeBreadcrumb(breadcrumb) {
            // Add mobile context to navigation breadcrumbs
            if (breadcrumb.category === 'navigation') {
                breadcrumb.data = {
                    ...breadcrumb.data,
                    isMobile: globalThis.innerWidth < 768,
                    connectionType: (navigator as any).connection?.effectiveType || 'unknown'
                };
            }
            return breadcrumb;
        },
    });

    // Set initial mobile context
    Sentry.setTag('device.mobile', globalThis.innerWidth < 768);
    Sentry.setContext('device', {
        screenWidth: globalThis.innerWidth,
        screenHeight: globalThis.innerHeight,
        pixelRatio: globalThis.devicePixelRatio,
        touchSupport: 'ontouchstart' in globalThis
    });

    // Commented out Sentry metrics calls that were causing "Method not found" errors
    // These require specific Sentry configuration and might not be available in all setups
    // Sentry.metrics.count('button_click', 1);
    // Sentry.metrics.gauge('page_load_time', 150);
    // Sentry.metrics.distribution('response_time', 200);

    // Add network monitoring for mobile connection issues
    addNetworkMonitoring();
}

createRoot(document.getElementById("root")).render(<App />);
